import React, { useEffect } from "react";
import { IonContent, IonLoading, IonPage } from "@ionic/react";
import axios from "axios";

/**
 * SMS follow-up CTA links for the Vita Coco campaign.
 *
 * A person who registered before receives SMS follow-ups. Each SMS contains a
 * short tracked link of the form:
 *
 *   <site>/<slug>/<model_user_id>
 *
 * where <slug> selects the CTA (see VITACOCO_FOLLOWUP_CTAS below) and
 * <model_user_id> is the consumer's plain numeric id. We forward it to the
 * server as `cons_model_user_id` (with `uuid` empty); registerConsumerCTA finds
 * the consumer's tracking row by this id
 * (sm_push_pulse_activity.ppa_consumer_user_id) instead of by the session uuid.
 *
 *   <site>/vcp/<model_user_id>    product
 *   <site>/vcs/<model_user_id>    store locator
 *   <site>/vcb/<model_user_id>    benefits
 *   <site>/vcr/<model_user_id>    reviews
 *   <site>/vcf/<model_user_id>    flavors
 *   <site>/vcpp/<model_user_id>   product (pineapple variant)
 *   <site>/vcpanel/<model_user_id> panel survey (Google Form)
 *
 * When the link is opened we read the id from the path, register the CTA click
 * (same POST /users/registerConsumerCTA call used by the existing trackCTAClick
 * methods) and then redirect the user to the destination page.
 */
export const VITACOCO_FOLLOWUP_CTAS = [
  {
    slug: "vcp",
    ctaType: "cta2",
    ctaLabel: "Vita Coco product link pressed",
    redirectUrl: "https://vitacoco.com/discount/SWEAT15",
  },
  {
    slug: "vcs",
    ctaType: "cta3",
    ctaLabel: "Vita Coco store locator link pressed",
    redirectUrl: "https://vitacoco.com/pages/store-locator",
  },
  {
    slug: "vcb",
    ctaType: "cta4",
    ctaLabel: "Vita Coco Benefits link pressed",
    redirectUrl: "https://vitacoco.com/account/register/",
  },
  {
    slug: "vcr",
    ctaType: "cta5",
    ctaLabel: "Vita Coco Reviews link pressed",
    redirectUrl: "https://vitacoco.com/pages/contact-us",
  },
  {
    slug: "vcf",
    ctaType: "cta6",
    ctaLabel: "Vita Coco flavor link pressed",
    redirectUrl: "https://vitacoco.com/collections/all",
  },
  {
    // Same CTA as vcp (cta2, same label) — only the product variant differs.
    slug: "vcpp",
    ctaType: "cta2",
    ctaLabel: "Vita Coco product link pressed",
    redirectUrl:
      "https://vitacoco.com/products/coconut-water?variant=40491707236540",
  },
  {
    // One-off "Vita Coco Panel" welcome SMS (cron/sendVitaCocoPanelWelcome in php-admin-api).
    slug: "vcpanel",
    ctaType: "cta7",
    ctaLabel: "Vita Coco Panel survey link pressed",
    redirectUrl: "https://forms.gle/18XG7PNCPDM54GjT8",
  },
];

const VitaCocoFollowUpCTA: React.FC = () => {
  useEffect(() => {
    (async () => {
      // Path is /<slug>/<model_user_id>, e.g. /vcp/12345.
      const parts = window.location.pathname.split("/").filter(Boolean);
      const slug = (parts[0] || "").toLowerCase();
      const consModelUserId = parts[1] || "";

      const config = VITACOCO_FOLLOWUP_CTAS.find(
        (cta) => cta.slug.toLowerCase() === slug
      );

      // Unknown route: nothing to redirect to, bail out.
      if (!config) {
        return;
      }

      // Register the CTA click — reuses the same endpoint as trackCTAClick.
      // We `await` the request so it fully completes BEFORE we navigate away:
      // redirecting mid-flight would let the browser cancel the request and
      // the CTA click would be lost. `try/catch` means a tracking failure
      // still lets the user through to the destination.
      if (consModelUserId) {
        try {
          await axios({
            method: "post",
            url: `${process.env.REACT_APP_HP_API_URL}/users/registerConsumerCTA`,
            data: {
              // `uuid` is intentionally empty; the server locates the consumer
              // by `cons_model_user_id` (their model_user_id) instead.
              uuid: "",
              cons_model_user_id: consModelUserId,
              ctaType: config.ctaType,
              ctaLabel: config.ctaLabel,
            },
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
        } catch (e) {
          // Ignore — we still want to send the user to the destination.
        }
      }

      // Only now that the tracking request has returned do we redirect.
      // `replace` keeps this tracking URL out of history so tapping Back
      // doesn't re-fire the CTA.
      window.location.replace(config.redirectUrl);
    })();
  }, []);

  return (
    <IonPage>
      <IonContent>
        <IonLoading isOpen={true} message={"Redirecting..."} />
      </IonContent>
    </IonPage>
  );
};

export default VitaCocoFollowUpCTA;
